import { type ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getMessageFromErrorResponse } from '@/api/v4/base-fetch';
import type { TemplateFileType } from '@/api/v4/template.api';
import { UploadFileInput } from '@/components/upload-file-input/upload-file-input';
import { ADOBE_EXTENSIONS } from '@/features/action-panel/files/constants';
import { useValidateUploadedFiles } from '@/features/action-panel/files/use-validate-uploaded-files';
import { getHttpErrorStatus } from '@/helpers/get-http-error-status';
import { isClientHttpError } from '@/helpers/is-client-http-error';
import { notify } from '@/ui/snackbar/notify';

import { useUploadTemplate } from '../use-upload-template.mutation';

interface TemplateUploadButtonProps {
  fileType: TemplateFileType;
}

export const TemplateUploadButton = ({
  fileType,
}: TemplateUploadButtonProps) => {
  const { t } = useTranslation('settings', {
    keyPrefix: 'manageTemplatesTab',
  });
  const { t: translateDefault } = useTranslation('default');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { validateUploadedFiles } = useValidateUploadedFiles(ADOBE_EXTENSIONS);
  const { uploadTemplate } = useUploadTemplate();

  const notifyUploadError = (error: unknown) => {
    if (getHttpErrorStatus(error) === 500) {
      return;
    }

    if (isClientHttpError(error)) {
      const message = getMessageFromErrorResponse(error);
      notify({ message: message || t`uploadFailed` });
      return;
    }

    notify({ message: translateDefault`unknownError` });
  };

  const onFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const uploadedFiles = validateUploadedFiles(event.target.files || []);
      if (!uploadedFiles) {
        return;
      }

      await uploadTemplate({ file: uploadedFiles[0], fileType });
    } catch (error) {
      notifyUploadError(error);
    } finally {
      event.target.value = '';
    }
  };

  return (
    <UploadFileInput
      ref={fileInputRef}
      variant="secondary"
      acceptedFiles={ADOBE_EXTENSIONS}
      buttonText={t`upload`}
      onClick={() => fileInputRef.current?.click()}
      onUploadFile={event => {
        void onFileInputChange(event);
      }}
      multiple={false}
      icon="Plus"
    />
  );
};
